import type { SalesChecklistSection } from "@expert/contentful";
import { Accordion, Group, Stack, Text } from "@mantine/core";
import { type JSX, useEffect } from "react";
import { ChecklistItem } from "@/sales-tools/home-product/components/FullOfferChecklist/components/ChecklistItem";
import { CompletionBadge } from "@/sales-tools/home-product/components/FullOfferChecklist/components/CompletionBadge";
import { CoverageCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/CoverageCarousel";
import { EngagementCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/EngagementCarousel";
import { RapportCarousel } from "@/sales-tools/home-product/components/FullOfferChecklist/components/carousels/RapportCarousel";
import { isSectionComplete } from "@/sales-tools/home-product/components/FullOfferChecklist/utils/isSectionComplete";
import {
    type OfferChecklistKey,
    type OfferChecklistState,
    useOfferChecklistStore,
} from "@/sales-tools/home-product/stores/useOfferChecklistStore";
import { useSessionId } from "@/sdk";

export function ChecklistSection({
    section,
    onSectionComplete,
}: {
    section: SalesChecklistSection;
    onSectionComplete: (title: string) => void;
}): JSX.Element | null {
    const sessionId = useSessionId();
    const data = useOfferChecklistStore((state) => state.getData(sessionId));
    const isCompleted = isSectionComplete(section.checklistItems, data);

    useEffect(() => {
        if (!isCompleted) return;
        onSectionComplete(section.title);
    }, [isCompleted, onSectionComplete, section.title]);

    return (
        <>
            <Accordion.Item key={section.title} value={section.title}>
                <Accordion.Control styles={{ control: { opacity: 1 } }}>
                    <Group justify="space-between" pr={"sm"}>
                        <Text size="xs" c="dark.1" fw="var(--mantine-font-weight-heavy)">
                            {section.title}
                        </Text>

                        <CompletionBadge
                            totalSteps={section.checklistItems.length}
                            completedSteps={getCompletedSteps(section, data)}
                        />
                    </Group>
                </Accordion.Control>

                <Accordion.Panel>
                    <Stack gap="xs">
                        {section.checklistItems.map((item) => (
                            <Stack gap={4} key={item.phrase}>
                                <ChecklistItem item={item} />

                                {item.key === "RAPPORT" && <RapportCarousel defaultExample={item.defaultExample} />}
                                {item.key === "COVERAGE" && <CoverageCarousel defaultExample={item.defaultExample} />}
                                {["DISCOVERY_QUESTION", "ENGAGEMENT_QUESTION"].includes(item.key) && (
                                    <EngagementCarousel examples={item.examples} />
                                )}
                            </Stack>
                        ))}
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>
        </>
    );
}

const getCompletedSteps = (section: SalesChecklistSection, data: OfferChecklistState["data"]) => {
    return section.checklistItems.filter((item) => data[item.key as OfferChecklistKey]).length;
};
